import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { PRIMARY_CATEGORIES } from "../constants";
import type { WizardData } from "../hooks/useWizardState";

interface Props {
  data: WizardData;
  updateData: (patch: Partial<WizardData>) => void;
  onNext: () => void;
}

export function SubcategorySelection({ data, updateData, onNext }: Props) {
  const { t } = useTranslation("addAsset");
  const category = PRIMARY_CATEGORIES.find(
    (c) => c.id === data.primaryCategory
  );
  const [otherText, setOtherText] = useState(
    data.subcategory === "other" ? data.subcategoryLabel : ""
  );

  if (!category) return null;

  const handleSelect = (subId: string, subLabel: string) => {
    if (subId === "other") {
      updateData({ subcategory: "other", subcategoryLabel: otherText || "Other", name: "", brand: "" });
      return;
    }
    updateData({
      subcategory: subId,
      subcategoryLabel: subLabel,
      name: "",
      brand: "",
    });
    setTimeout(onNext, 200);
  };

  const isOtherSelected = data.subcategory === "other";

  return (
    <div className="animate-fade-in-up">
      <div className="px-6 pb-3 pt-4">
        <h2 className="text-2xl font-light tracking-tight">
          {t("assetType.selectAssetType")}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t("assetType.whatTypeOf", {
            category: t(`assetType.cats.${category.id}.label`, { defaultValue: category.label }).toLowerCase(),
          })}
        </p>
      </div>

      <div className="px-6 pb-6 grid grid-cols-3 gap-3 mt-5">
        {category.subcategories.map((sub, idx) => {
          const Icon = sub.icon;
          const isOther = sub.id === "other";
          const isSelected = isOther ? isOtherSelected : data.subcategory === sub.id;

          return (
            <button
              key={sub.id}
              onClick={() => handleSelect(sub.id, sub.label)}
              style={{ animationDelay: `${idx * 0.05}s` }}
              className={cn(
                "animate-fade-in-up flex flex-col items-center justify-center gap-2.5 p-4 rounded-2xl border-2 transition-all duration-200 cursor-pointer group shadow-sm hover:shadow-md",
                "hover:border-primary/50 hover:bg-primary/[0.03] hover:shadow-sm",
                isSelected
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border bg-background"
              )}
            >
              <div
                className={cn(
                  "w-10 h-10 rounded-xl flex items-center justify-center transition-colors",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
              <span className="text-sm font-medium">{t(`assetType.subs.${sub.id}`, { defaultValue: sub.label })}</span>
            </button>
          );
        })}
      </div>

      {/* Free-text input when "Other" is selected */}
      {isOtherSelected && (
        <div className="px-6 pb-6 -mt-3 space-y-3 animate-fade-in-up">
          <input
            type="text"
            autoFocus
            value={otherText}
            onChange={(e) => {
              setOtherText(e.target.value);
              updateData({ subcategoryLabel: e.target.value || t("assetType.subs.other", { defaultValue: "Other" }) });
            }}
            placeholder={t("assetType.subs.other", { defaultValue: "Other" }) + " — e.g. Garbage Disposal, Sump Pump…"}
            className="w-full px-4 py-2.5 rounded-xl border-2 border-primary/40 bg-background text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            onClick={onNext}
            disabled={!otherText.trim()}
            className="w-full py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium disabled:opacity-40"
          >
            {t("common.continue", { defaultValue: "Continue" })}
          </button>
        </div>
      )}
    </div>
  );
}
